/* eslint-disable react-hooks/exhaustive-deps */
import { Dropdown } from "."
import { classNames } from "../utilities"
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
} from "@heroicons/react/outline"
import Link from "next/link"
import React, { CSSProperties } from "react"
import { Carousel } from "react-responsive-carousel"
import "react-tabs/style/react-tabs.css"

interface ProductData {
  img: string
  details: string
  excerpt: string
  features: string[]
  basePrice: number
  url: string
}

interface ProductDataDict {
  [key: string]: ProductData
}

interface Props {
  products?: string[]
  quoteUrl: string
  companyType?: string
}

//Function Expression
export function ProductCarousel({
  products = [
    "General Liability",
    "Business Owner's Policy",
    "Workers' Compensation",
    "Cyber",
    "Professional Liability",
    "Management Liability",
  ],
  quoteUrl,
  companyType,
}: Props) {
  const productData: ProductDataDict = {
    "General Liability": {
      img: "/img/sb1.jpg",
      details: `General Liability insurance protects businesses of all sizes and industries from property damage, customer injury, and lawsuits. It's a crucial policy for overall workplace protection.`,
      excerpt: `Core financial protection from everyday workplace risks.`,
      basePrice: 19,
      features: [
        "Lender, contract, or landlord requirements",
        "Customer injuries or product liability",
        "Reputational damage and legal disputes",
      ],
      url: "/general-liability",
    },
    "Business Owner's Policy": {
      img: "/img/sb5.jpg",
      details: `Business Owner's Policy (BOP) is the most common bundled policy and includes general liability, property / inventory, and business interruption coverage.`,
      excerpt: `Most common bundled coverage (liability, property, inventory, and more).`,
      basePrice: 25,
      features: [
        "Lender, contract, or landlord requirements",
        "Customer injuries or product liability",
        "Property damage or inventory replacement",
      ],
      url: "/business-policy",
    },
    "Workers' Compensation": {
      img: "/img/sb4.jpg",
      details: `Workers' Compensation insurance covers costs of medical care and lost wages to employees that suffer any injury or illness as a direct result of employment.`,
      excerpt: `Covers injuries or illness as a direct result of employment`,
      basePrice: 15,
      features: [
        "Mandated U.S. state requirements",
        "Employee injury or medical expenses",
      ],
      url: "/workers-compensation",
    },
    Cyber: {
      img: "/img/sb3.jpg",
      details: `Cyber insurance covers your business from data loss, breaches, phishing, tech-related threats, and all other virtual risks your business faces.`,
      excerpt: `Protects against malicious cyberattacks, loss of data, and more.`,
      basePrice: 20,
      features: [
        "Vendor, lender, contract, or business requirements",
        "System hacks, data breach, or ransomware",
        "Phishing, digital funds transfer, or fraud",
      ],
      url: "/cyber",
    },
    "Professional Liability (E&O)": {
      img: "/img/sb2.jpg",
      details: `Professional Liability, also known as Errors & Omissions, covers your business from customer legal disputes arising from negligence or mistakes.`,
      excerpt: `Covers your business from customer legal disputes.`,
      basePrice: 17,
      features: [
        "Vendor, contract, or consulting requirements",
        "Negligence, unfulfilled tasks, or mistakes",
        "Legal disputes",
      ],
      url: "/professional-liability",
    },
    "Management Liability": {
      img: "/img/mgl.jpg",
      details: `Management liability insurance, also known as directors and officers (D&O) insurance, provides financial protection to the company's management team against claims related to management decisions.`,
      excerpt: `Includes Directors & Officers, EPLI, Fiduciary, and Crime.`,
      basePrice: 27,
      features: [
        "Fulfills investor requirements",
        "Covers claims from government bodies",
        "Protects founders from unanticipated lawsuits",
      ],
      url: "/management-liability",
    },
  }

  const productDataEcom: ProductDataDict = {
    "General Liability": {
      img: "/img/sb1.jpg",
      details: `General Liability insurance protects e-commerce businesses and sellers of all sizes and industries from property damage, product liability, customer injury, and lawsuits. It's a crucial policy and universally required.`,
      excerpt: `Core financial protection from everyday e-commerce business risks.`,
      basePrice: 19,
      features: [
        "Online seller marketplace requirements",
        "Customer injuries or product liability",
        "Lender, contract, or landlord requirements",
      ],
      url: "/general-liability",
    },
    "Business Owner's Policy": {
      img: "/img/sb5.jpg",
      details: `Business Owner's Policy (BOP) is the most common bundled policy and includes general liability, property / inventory, and business interruption coverage.`,
      excerpt: `Most common bundled coverage (liability, property, inventory, and more). `,
      basePrice: 25,
      features: [
        "Online seller marketplace requirements",
        "Property damage or inventory replacement",
        "Customer injuries or product liability",
      ],
      url: "/business-policy",
    },
    "Workers' Compensation": {
      img: "/img/sb4.jpg",
      details: `Workers' Compensation insurance covers costs of medical care and lost wages to employees that suffer any injury or illness as a direct result of employment.`,
      excerpt: `Covers injuries or illness as a direct result of employment`,
      basePrice: 15,
      features: [
        "Online seller marketplace requirements",
        "Mandated U.S. state requirements",
        "Employee injury or medical expenses",
      ],
      url: "/workers-compensation",
    },
    Cyber: {
      img: "/img/sb3.jpg",
      details: `Cyber insurance covers your e-commerce business from data loss, breaches, phishing, tech-related threats, and all other virtual risks your business faces.`,
      excerpt: `Protects your online storefront from malicious cyberattacks, loss of data, and more.`,
      basePrice: 20,
      features: [
        "Online seller marketplace requirements",
        "Vendor, lender, contract, or business requirements",
        "Phishing, digital funds transfer, or fraud",
      ],
      url: "/cyber",
    },
    "Professional Liability (E&O)": {
      img: "/img/sb2.jpg",
      details: `Professional Liability, also known as Errors & Omissions, covers your business from customer legal disputes arising from negligence or mistakes.`,
      excerpt: `Covers your business from customer legal disputes.`,
      basePrice: 17,
      features: [
        "Vendor, contract, or consulting requirements",
        "Negligence, unfulfilled tasks, or mistakes",
        "Legal disputes",
      ],
      url: "/professional-liability",
    },
    "Management Liability": {
      img: "/img/mgl.jpg",
      details: `Management liability insurance, also known as directors and officers (D&O) insurance, provides financial protection to the company's management team against claims related to management decisions.`,
      excerpt: `Includes Directors & Officers, EPLI, Fiduciary, and Crime.`,
      basePrice: 27,
      features: [
        "Fulfills investor requirements",
        "Covers claims from government bodies",
        "Protects founders from unanticipated lawsuits",
      ],
      url: "/management-liability",
    },
  }

  const [currentTab, setCurrentTab] = React.useState(
    companyType === "startup" ? "Management Liability" : "General Liability"
  )
  const arrowStyles: CSSProperties = {
    position: "absolute",
    zIndex: 2,
    top: "calc(50% - 15px)",
    width: 30,
    height: 30,
    cursor: "pointer",
  }
  const [currentSlide, setCurrentSlide] = React.useState(0)

  function updateCurrentSlide(index: number) {
    if (currentSlide !== index) {
      setCurrentSlide(index)
      setCurrentTab(products[index])
    }
  }

  React.useEffect(() => {
    const productIndex = products.indexOf(currentTab)
    setCurrentSlide(productIndex)
  }, [currentTab])
  return (
    <div>
      {/* TABS */}
      <div>
        <div className="lg:hidden">
          <label htmlFor="tabs" className="sr-only">
            Select a tab
          </label>
          <Dropdown options={products} onChange={setCurrentTab} />
        </div>
        <div className="hidden lg:block">
          <nav className="flex space-x-4 justify-center" aria-label="Tabs">
            {products.map((tab) => (
              <a
                key={tab}
                role="button"
                className={classNames(
                  tab === currentTab
                    ? "bg-green-50 text-alt border-2 border-primary"
                    : "border-2 border-transparent text-gray-500 hover:bg-green-50 hover:text-alt transition duration-150 ease-in-out",
                  "px-3 py-2 font-medium rounded-md"
                )}
                aria-current={tab === currentTab ? "page" : undefined}
                onClick={() => setCurrentTab(tab)}
              >
                {tab}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* CONTENT */}
      <div className="mt-12">
        <Carousel
          showStatus={false}
          showArrows
          showIndicators
          infiniteLoop
          autoPlay
          interval={15000}
          transitionTime={1000}
          renderArrowPrev={(onClickHandler, hasPrev, label) =>
            hasPrev && (
              <button
                type="button"
                onClick={onClickHandler}
                title={label}
                className="hidden lg:block"
                style={{ ...arrowStyles, left: 0 }}
              >
                <ArrowLeftIcon className="text-primary" />
              </button>
            )
          }
          selectedItem={currentSlide}
          renderArrowNext={(onClickHandler, hasNext, label) =>
            hasNext && (
              <button
                type="button"
                onClick={onClickHandler}
                title={label}
                className="hidden lg:block"
                style={{ ...arrowStyles, right: 15 }}
              >
                <ArrowRightIcon className="text-primary" />
              </button>
            )
          }
          onChange={updateCurrentSlide}
          renderIndicator={(
            clickHandler: (e: React.MouseEvent | React.KeyboardEvent) => void,
            isSelected: boolean
          ) => (
            <button
              onClick={clickHandler}
              type="button"
              className={classNames(
                isSelected ? "bg-primary" : "bg-white",
                "mx-1 w-2 h-2 ring-1 ring-primary rounded-full"
              )}
            />
          )}
        >
          {products.map((productName) => {
            const product =
              companyType === "ecommerce"
                ? productDataEcom[productName]
                : productData[productName]
            return (
              <div
                key={productName}
                className="mt-2 max-w-4xl mx-auto pb-24 sm:pb-12"
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 grid-flow-row auto-rows-min gap-4 md:gap-8 lg:gap-12 lg:space-x-12 space-y-6">
                  <div className="aspect-square text-left row-span-1 lg:row-span-2">
                    {/* <FadeIn> */}
                    <img
                      className="sm:w-96 object-cover shadow-lg rounded-lg"
                      src={product.img}
                      alt={productName}
                      aria-hidden="true"
                    />
                    <p className="mt-6 text-xl font-medium text-primary">
                      {productName}
                    </p>
                    <div className="text-base mt-2 max-w-lg text-gray-400">
                      {product.excerpt}
                    </div>
                    <p
                      className={classNames(
                        "text-base font-semibold mt-2 max-w-lg",
                        productName === "Management Liability" ? "mb-8" : ""
                      )}
                    >
                      Starting at{" "}
                      <span className="mx-1 text-xl font-bold text-primary">
                        ${product.basePrice}
                      </span>{" "}
                      per month{" "}
                    </p>
                    {/* </FadeIn> */}
                  </div>

                  <div className="relative col-span-2 h-[20rem] flex flex-col justify-around order-2 md:order-1">
                    {/* <FadeIn delay={0.2}> */}
                    <p className="text-left text-base mt-2 max-w-3xl">
                      {product.details}
                    </p>
                    <p className="text-left mt-3 uppercase tracking-wider text-xs font-semibold text-gray-400">
                      Coverage for:
                    </p>
                    <ul
                      role="list"
                      className={classNames("my-2 text-sm tracking-tight")}
                    >
                      {product.features.map((feature) => (
                        <li key={feature} className="flex py-2 text-left">
                          <CheckCircleIcon
                            className={classNames(
                              "h-5 w-5 flex-none text-primary"
                            )}
                          />
                          <span className="ml-2">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-6 sm:flex px-2 sm:px-0">
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={quoteUrl}
                        className="btn-primary w-full sm:w-auto"
                      >
                        Get a quote
                      </a>

                      <div className="mt-3 sm:mt-0 sm:ml-3">
                        <Link
                          href={product.url}
                          className="btn-secondary w-full sm:w-auto"
                        >
                          Learn more
                        </Link>
                      </div>
                    </div>
                    {/* </FadeIn> */}
                  </div>
                </div>
              </div>
            )
          })}
        </Carousel>
      </div>
    </div>
  )
}
